
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { securityService } from '@/services/securityService';
import { toast } from '@/components/ui/sonner';

export const useSecureAuth = () => {
  const [isLoading, setIsLoading] = useState(false);

  const secureSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      // Check rate limiting
      const rateLimitCheck = securityService.canAttemptLogin(email);
      
      if (!rateLimitCheck.allowed) {
        const lockoutTime = rateLimitCheck.lockoutUntil;
        toast.error(`Too many failed attempts. Try again after ${lockoutTime?.toLocaleTimeString()}`);
        securityService.recordLoginAttempt(email, false);
        return { error: { message: 'Rate limit exceeded' } };
      }

      // Validate password strength for existing users (optional check)
      const passwordValidation = securityService.validatePassword(password);
      if (!passwordValidation.isValid) {
        // Log weak password usage
        await securityService.logSecurityEvent('weak_password_usage', undefined, {
          email: email.substring(0, 3) + '***', // Partial email for privacy
          errors: passwordValidation.errors
        });
      }

      // Perform authentication
      const { data, error } = await supabase.auth.signInWithPassword({
        email: securityService.sanitizeText(email),
        password
      });

      // Record the attempt
      securityService.recordLoginAttempt(email, !error);

      if (error) {
        await securityService.logSecurityEvent('login_failed', undefined, {
          email: email.substring(0, 3) + '***',
          error: error.message
        });
        
        const remaining = securityService.canAttemptLogin(email).remainingAttempts;
        if (remaining !== undefined && remaining <= 2) {
          toast.warning(`${remaining} attempts remaining before account lockout`);
        }
        
        return { error };
      }

      // Log successful login
      await securityService.logSecurityEvent('login_successful', data.user?.id, {
        email: email.substring(0, 3) + '***'
      });

      return { data, error: null };
    } catch (err) {
      console.error('Secure sign in error:', err);
      securityService.recordLoginAttempt(email, false);
      return { error: { message: 'An unexpected error occurred' } };
    } finally {
      setIsLoading(false);
    }
  };

  const secureSignUp = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    
    try {
      // Validate password strength
      const passwordValidation = securityService.validatePassword(password);
      if (!passwordValidation.isValid) {
        toast.error('Password requirements not met:\n' + passwordValidation.errors.join('\n'));
        return { error: { message: 'Password does not meet security requirements' } };
      }

      // Sanitize inputs
      const sanitizedEmail = securityService.sanitizeText(email);
      const sanitizedName = securityService.sanitizeText(name);

      const { data, error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          data: {
            name: sanitizedName,
            display_name: sanitizedName,
          },
          emailRedirectTo: 'https://neoprismacademy.com/dashboard'
        }
      });

      if (error) {
        await securityService.logSecurityEvent('signup_failed', undefined, {
          email: email.substring(0, 3) + '***',
          error: error.message
        });
        return { error };
      }

      // Log successful signup
      await securityService.logSecurityEvent('signup_successful', data.user?.id, {
        email: email.substring(0, 3) + '***'
      });

      return { data, error: null };
    } catch (err) {
      console.error('Secure sign up error:', err);
      return { error: { message: 'An unexpected error occurred' } };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    secureSignIn,
    secureSignUp,
    isLoading
  };
};
